// packages/ui/src/components/Flag.tsx
import type { CSSProperties } from 'react';

import { FlagCore, type FlagSubject } from '../primitives/flags/FlagCore';

/* ───────────────────────────────────────────────────────────────────────────
 * READ THIS BEFORE USING `Flag` DIRECTLY — applies to humans AND AI agents.
 *
 * If you are about to render a flag with an adjacent country/language label
 * (e.g. flag + "Spain", flag + "English") — STOP and use `FlagWithLabel`
 * from `@tourtask/ui/components/FlagWithLabel` instead. Never roll your own
 * `<Box>{flag}<span>{name}</span></Box>` combo. The standardized component
 * exists specifically so that flag size, alignment, gap, and typography
 * stay identical across the entire app. Hand-rolled flag+label variants
 * have drifted apart over time and are explicitly forbidden going forward.
 * Where MUI cannot load (a hosted website, apps/sites), the same layout is
 * `FlagWithLabelCore` from `@tourtask/ui/primitives/flags/FlagWithLabelCore`,
 * which `FlagWithLabel` itself is built on.
 *
 * `Flag` (this component) is a low-level primitive. Use it ONLY for:
 *   - icon-only roles where there is no adjacent label in the same Box
 *     (e.g. inside MUI `<ListItemIcon>`, as a Button `startIcon`, inside a
 *     Tooltip target, or as the `getSelectedAdornment` of an Autocomplete
 *     where the label is rendered separately by the input).
 *
 * If you are not sure which to use: use `FlagWithLabel`. The default is
 * always `FlagWithLabel`; reaching for raw `Flag` requires justification.
 *
 * The image itself is `FlagCore` (`@tourtask/ui/primitives/flags/FlagCore`,
 * no MUI): this component is that core with react-country-flag's default
 * source, the flag-icons files on jsDelivr.
 *
 * The language → flag-country mapping is NOT here: it lives in
 * `@tourtask/utils/shared/flags/languageToCountryCode` so the native app's
 * `Flag` (`@tourtask/mobile-ui`, which cannot import MUI) picks the same flag
 * for the same language. Add new languages there, not in this file. An
 * unmapped language renders a blank flag-sized slot, as the native `Flag` does.
 * ─────────────────────────────────────────────────────────────────────── */

type FlagProps = FlagSubject & { style?: CSSProperties; disabled?: boolean };

export function Flag(props: FlagProps) {
  return <FlagCore {...props} />;
}
